import dicomParser from 'dicom-parser'
import { buildDicomWebBaseUrl } from '../../config/pacsConfig'
import { pacsAuthHeaders } from './pacsAuth'

/**
 * Extrae StudyInstanceUID (0020,000D) de bytes DICOM Part 10.
 * @param {Uint8Array|ArrayBuffer} bytes
 * @returns {string|null}
 */
export function parseStudyInstanceUidFromBytes(bytes) {
  if (!bytes) return null
  try {
    const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
    const dataSet = dicomParser.parseDicom(arr)
    const uid = dataSet && dataSet.string('x0020000d')
    return uid ? String(uid).trim() : null
  } catch (e) {
    return null
  }
}

function utf8ToBytes(str) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str)
  }
  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(String(str), 'utf8'))
  }
  const s = String(str)
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i)
  return out
}

/**
 * Construye cuerpo multipart/related para STOW-RS.
 * @param {Uint8Array} bytes
 * @param {string} boundary
 * @returns {Uint8Array}
 */
export function buildStowMultipartBody(bytes, boundary) {
  const prefix = utf8ToBytes(
    `--${boundary}\r\nContent-Type: application/dicom\r\n\r\n`
  )
  const suffix = utf8ToBytes(`\r\n--${boundary}--\r\n`)
  const body = new Uint8Array(prefix.length + bytes.length + suffix.length)
  body.set(prefix, 0)
  body.set(bytes, prefix.length)
  body.set(suffix, prefix.length + bytes.length)
  return body
}

/**
 * Almacena instancia DICOM via STOW-RS.
 * 2xx → true; 404/405 → false (degradar); otros → false.
 *
 * @param {Uint8Array} bytes
 * @param {Object|null} pacsConfig
 * @param {{ dicomWebBase?: string, wadoUrl?: string }} [options]
 * @returns {Promise<boolean>}
 */
export async function stowStoreDicomInstance(bytes, pacsConfig, options = {}) {
  if (!pacsConfig || !bytes || !bytes.length) {
    return false
  }

  const studyUid = parseStudyInstanceUidFromBytes(bytes)
  if (!studyUid) {
    console.warn('[stowStoreDicomInstance] No StudyInstanceUID en bytes')
    return false
  }

  const dicomWebBase = (
    options.dicomWebBase ||
    buildDicomWebBaseUrl(pacsConfig, options.wadoUrl)
  ).replace(/\/$/, '')
  const url = `${dicomWebBase}/studies/${encodeURIComponent(studyUid)}`
  const boundary = `UDVStow${Date.now()}`
  const body = buildStowMultipartBody(bytes, boundary)

  const headers = {
    ...pacsAuthHeaders(pacsConfig),
    'Content-Type': `multipart/related; type="application/dicom"; boundary=${boundary}`,
  }

  let response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    })
  } catch (networkError) {
    console.warn('[stowStoreDicomInstance] Error de red:', networkError)
    return false
  }

  if (response.status >= 200 && response.status < 300) {
    return true
  }

  if (response.status === 404 || response.status === 405) {
    return false
  }

  console.warn(
    `[stowStoreDicomInstance] Respuesta inesperada del PACS: ${response.status}`
  )
  return false
}
